import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import StatusBadge, { formatCurrency } from "@/components/StatusBadge";
import { useTableSort } from "@/lib/useTableSort";

export default function ClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [projectQ, setProjectQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/api/clients/${id}`).then((r) => setData(r.data));
  }, [id]);

  const projects = data?.projects || [];

  // Client-wide rollup: total billed vs total current value across all projects
  const rollup = useMemo(() => {
    const cv = projects.reduce((s, p) => s + Number(p.current_project_value ?? p.project_amount ?? 0), 0);
    const billed = projects.reduce((s, p) => s + Number(p.total_invoiced || 0), 0);
    const received = projects.reduce((s, p) => s + Number(p.total_received || 0), 0);
    return {
      current_value: cv,
      billed,
      received,
      delta: Math.round((billed - cv) * 100) / 100,
      over: projects.filter((p) => Number(p.total_invoiced || 0) > Number(p.current_project_value ?? p.project_amount ?? 0) + 0.01).length,
      under: projects.filter((p) => Number(p.total_invoiced || 0) < Number(p.current_project_value ?? p.project_amount ?? 0) - 0.01 && Number(p.total_invoiced || 0) > 0).length,
    };
  }, [projects]);

  const statuses = useMemo(() => {
    const s = new Set(projects.map((p) => p.status).filter(Boolean));
    return Array.from(s).sort();
  }, [projects]);

  const filteredProjects = useMemo(() => {
    const q = projectQ.trim().toLowerCase();
    return projects.filter((p) => {
      if (statusFilter !== "all" && (p.status || "") !== statusFilter) return false;
      if (q) {
        const hay = `${p.project_name || ""} ${p.project_code || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [projects, projectQ, statusFilter]);

  const projectColumns = [
    { key: "project_code", label: "Code" },
    { key: "project_name", label: "Project" },
    { key: "current_project_value", label: "Value", numeric: true, align: "right", getValue: (p) => p.current_project_value ?? p.project_amount ?? 0 },
    { key: "total_invoiced", label: "Billed", numeric: true, align: "right", getValue: (p) => p.total_invoiced || 0 },
    { key: "delta", label: "Δ vs Value", numeric: true, align: "right", getValue: (p) => (Number(p.total_invoiced || 0) - Number(p.current_project_value ?? p.project_amount ?? 0)) },
    { key: "status", label: "Status" },
    { key: "payment_status", label: "Payment" },
    { key: "created_at", label: "Created", getValue: (p) => p.created_at || "" },
  ];
  const { sortedRows: sortedProjects, header: projectsHeader } =
    useTableSort(filteredProjects, projectColumns, "created_at", "desc", { trailingActionColumn: false });

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const { client, invoices, totals } = data;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div>
        <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900">← Back</button>
        <h1 className="text-3xl font-heading font-semibold mt-1">{client.firm_name}</h1>
        <div className="text-sm text-slate-500 mt-1">{client.contact_person_name} · {client.email} · {client.phone}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-none p-5">
          <div className="text-xs uppercase text-slate-500">Total Business</div>
          <div className="font-heading text-2xl font-semibold mt-1">{formatCurrency(totals.business, client.default_currency)}</div>
        </Card>
        <Card className="border-slate-200 shadow-none p-5">
          <div className="text-xs uppercase text-slate-500">Collected</div>
          <div className="font-heading text-2xl font-semibold mt-1 text-emerald-700">{formatCurrency(totals.collected, client.default_currency)}</div>
        </Card>
        <Card className="border-slate-200 shadow-none p-5">
          <div className="text-xs uppercase text-slate-500">Outstanding</div>
          <div className="font-heading text-2xl font-semibold mt-1 text-amber-700">{formatCurrency(totals.outstanding, client.default_currency)}</div>
        </Card>
        <Card
          className={`border-slate-200 shadow-none p-5 ${rollup.delta > 0.01 ? "bg-rose-50/40" : rollup.delta < -0.01 ? "bg-amber-50/40" : ""}`}
          data-testid="client-rollup-card"
        >
          <div className="text-xs uppercase text-slate-500">Billed vs Current Value</div>
          <div
            className={`font-heading text-2xl font-semibold mt-1 ${rollup.delta > 0.01 ? "text-rose-700" : rollup.delta < -0.01 ? "text-amber-700" : "text-emerald-700"}`}
            data-testid="client-rollup-delta"
          >
            {rollup.delta === 0
              ? "In sync"
              : `${rollup.delta > 0 ? "+" : ""}${formatCurrency(rollup.delta, client.default_currency)}`}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Billed {formatCurrency(rollup.billed, client.default_currency)} · Value {formatCurrency(rollup.current_value, client.default_currency)}
            {(rollup.over > 0 || rollup.under > 0) && (
              <> · <span className="text-rose-700">{rollup.over} over</span> · <span className="text-amber-700">{rollup.under} under</span></>
            )}
          </div>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-none p-4">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div className="font-heading font-medium">Projects ({filteredProjects.length} of {projects.length})</div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Search</Label>
              <Input
                placeholder="Project name or code…"
                value={projectQ}
                onChange={(e) => setProjectQ(e.target.value)}
                data-testid="client-projects-search"
                className="w-56"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44" data-testid="client-projects-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {statuses.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <table className="data-table w-full">
          <thead>{projectsHeader}</thead>
          <tbody>
            {sortedProjects.map((p) => {
              const cv = Number(p.current_project_value ?? p.project_amount ?? 0);
              const billed = Number(p.total_invoiced || 0);
              const delta = Math.round((billed - cv) * 100) / 100;
              const over = delta > 0.01;
              const under = delta < -0.01 && billed > 0;
              return (
                <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="cursor-pointer" data-testid={`client-project-row-${p.id}`}>
                  <td className="font-mono text-xs">{p.project_code}</td>
                  <td className="font-medium">{p.project_name}</td>
                  <td className="text-right tabular-nums">{formatCurrency(cv, p.currency)}</td>
                  <td className="text-right tabular-nums">{formatCurrency(billed, p.currency)}</td>
                  <td className="text-right tabular-nums">
                    <span
                      className={over ? "text-rose-700 font-semibold" : under ? "text-amber-700 font-medium" : "text-emerald-700"}
                      title={over ? "Over-billed: invoices exceed current project value" : under ? "Under-billed: room left to invoice" : "Fully billed to project value"}
                      data-testid={`delta-${p.id}`}
                    >
                      {delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${formatCurrency(delta, p.currency)}`}
                    </span>
                  </td>
                  <td><StatusBadge status={p.status} /></td>
                  <td><StatusBadge status={p.payment_status} /></td>
                  <td className="text-slate-500 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              );
            })}
            {sortedProjects.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-8">No projects match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card className="border-slate-200 shadow-none p-4">
        <div className="font-heading font-medium mb-3">Invoices ({invoices.length})</div>
        <table className="data-table w-full">
          <thead><tr><th>Invoice #</th><th>Amount</th><th>Received</th><th>Outstanding</th><th>Status</th></tr></thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id}>
                <td className="font-mono text-xs">{i.invoice_number}</td>
                <td>{formatCurrency(i.total_invoice_amount, i.currency)}</td>
                <td className="text-emerald-700">{formatCurrency(i.amount_received, i.currency)}</td>
                <td className="text-amber-700">{formatCurrency(i.outstanding_amount, i.currency)}</td>
                <td><StatusBadge status={i.payment_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
